import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../../services/api';
import BuildGraphMenu from './BuildGraphMenu';
import SynapsesTable from '../graph/synapses/SynapsesTable';
import NodesTable from '../graph/node/NodesTable';
import CanonicalTermTable from '../graph/dictionary/CanonicalTermTable';
import DictionaryEntryTable from '../graph/dictionary/DictionaryEntryTable';
import CanonicalTermModal from '../graph/dictionary/CanonicalTermModal';
import DictionaryEntryModal from '../graph/dictionary/DictionaryEntryModal';
import GraphPromptsModal from './GraphPromptsModal';
import AuditDashboard from '../explainability/AuditDashboard';
import './BuildGraph.css';
import '../graph/dictionary/DictionaryPage.css';


function BuildGraphPlaceholderPage() {
  const { graphId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('synapses');
  const [dictionaryTab, setDictionaryTab] = useState('canonical-terms');
  const synapsesTableRef = useRef(null);
  
  // Dictionary modal states
  const [showCanonicalTermModal, setShowCanonicalTermModal] = useState(false);
  const [showDictionaryEntryModal, setShowDictionaryEntryModal] = useState(false);
  const [selectedCanonicalTerm, setSelectedCanonicalTerm] = useState(null);
  const [selectedDictionaryEntry, setSelectedDictionaryEntry] = useState(null);
  const [dictionaryRefreshKey, setDictionaryRefreshKey] = useState(0);
  
  // Graph Prompts modal state
  const [showGraphPromptsModal, setShowGraphPromptsModal] = useState(false);

  // Extract category and subItem from pathname
  const pathParts = location.pathname.split('/');
  const graphIndex = pathParts.indexOf('build');
  const category = graphIndex >= 0 && pathParts[graphIndex + 2] ? pathParts[graphIndex + 2] : null;
  const subItem = graphIndex >= 0 && pathParts[graphIndex + 3] ? pathParts[graphIndex + 3] : null;

  // Map route paths to display names
  const getDisplayName = (categoryKey, itemPath) => {
    const categoryMap = {
      'build-tune': {
        'expand-graph': 'Expand Graph',
        'change-nodes-edges': 'Change Nodes/Edges',
        'explainability': 'Explainability',
        'graph-prompts': 'Graph Prompts'
      },
      'train-test': {
        'review-feedback': 'Review Feedback',
        'feedback-training': 'Review Feedback',
        'test-graph': 'Test Graph',
        'test-cases': 'Test Cases'
      },
      'connect-stream': {
        'endpoints': 'Endpoints',
        'graph-links': 'Graph Links'
      },
      'governance-logs': {
        'audit-logs': 'Audit Logs',
        'dictionary': 'Dictionary'
      },
      'deploy-versions': {
        'copy-to-environment': 'Copy to Environment'
      }
    };

    return categoryMap[categoryKey]?.[itemPath] || itemPath;
  };

  const pageName = category && subItem ? getDisplayName(category, subItem) : 'Build Graph';

  const loadGraphDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getGraphDetails(graphId);
      if (response.success) {
        setGraph(response.graph);
      } else {
        setError('Failed to load graph details');
      }
    } catch (err) {
      console.error('Error loading graph:', err);
      setError(err.response?.data?.message || 'Failed to load graph details');
    } finally {
      setLoading(false);
    }
  }, [graphId]);

  useEffect(() => {
    loadGraphDetails();
  }, [loadGraphDetails]);

  // Auto-open Graph Prompts modal when on that route
  useEffect(() => {
    if (subItem === 'graph-prompts') {
      setShowGraphPromptsModal(true);
    }
  }, [subItem]);

  const handleBack = () => {
    navigate(`/user-home/graphs/build/${graphId}`);
  };

  // Dictionary handlers
  const handleAddCanonicalTerm = () => {
    setSelectedCanonicalTerm(null);
    setShowCanonicalTermModal(true);
  };

  const handleEditCanonicalTerm = (term) => {
    setSelectedCanonicalTerm(term);
    setShowCanonicalTermModal(true);
  };

  const handleAddDictionaryEntry = () => {
    setSelectedDictionaryEntry(null);
    setShowDictionaryEntryModal(true);
  };

  const handleEditDictionaryEntry = (entry) => {
    setSelectedDictionaryEntry(entry);
    setShowDictionaryEntryModal(true);
  };

  const handleCanonicalTermSaved = () => {
    setShowCanonicalTermModal(false);
    setSelectedCanonicalTerm(null);
    setDictionaryRefreshKey(prev => prev + 1);
  };

  const handleDictionaryEntrySaved = () => {
    setShowDictionaryEntryModal(false);
    setSelectedDictionaryEntry(null);
    setDictionaryRefreshKey(prev => prev + 1);
  };

  if (loading) {
    return (
      <div className="build-graph-page">
        <BuildGraphMenu workspaceId={graph?.workspaceId} />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading graph...</p>
        </div>
      </div>
    );
  }

  if (error && !graph) {
    return (
      <div className="build-graph-page">
        <BuildGraphMenu workspaceId={graph?.workspaceId} />
        <div className="page-header">
          <h1 className="page-title">{pageName}</h1>
        </div>
        <div className="alert alert-error">{error}</div>
        <button className="btn-primary" onClick={handleBack}>
          Back to Build Graph
        </button>
      </div>
    );
  }

  return (
    <div className="build-graph-page">
      <BuildGraphMenu workspaceId={graph?.workspaceId} />
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">{pageName}</h1>
          {graph && <p className="page-subtitle">{graph.graphName}</p>}
        </div>
        <div className="header-right">
          <button className="btn-secondary" onClick={handleBack}>
            Back to Build Graph
          </button>
        </div>
      </div>

      {/* Graph Info */}
      {graph && (
        <div className="graph-info-card">
          <h3>Graph Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Workspace:</span>
              <span className="info-value">{graph.workspaceName || 'N/A'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Type:</span>
              <span className="info-value">{graph.graphType}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Status:</span>
              <span className="info-value">{graph.status}</span>
            </div>
            {graph.description && (
              <div className="info-item full-width">
                <span className="info-label">Description:</span>
                <span className="info-value">{graph.description}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Compliance Section */}
      {subItem === 'compliance' ? (
        <AuditDashboard
          graphId={graphId}
          onViewEvaluation={(evalItem) => {
            const insightParam = evalItem?.insight_id || evalItem?.execution_id || '';
            const explainUrl = `/user-home/graphs/build/${graphId}/build-tune/explainability${insightParam ? `?insight=${insightParam}` : ''}`;
            window.open(explainUrl, '_blank');
          }}
          onVerifyIntegrity={(traceId) => {
            navigate(`/user-home/graphs/build/${graphId}/governance-logs/audit-logs`);
          }}
        />
      ) : null}

      {/* Dictionary Section */}
      {subItem === 'dictionary' ? (
        <div className="dictionary-content">
          <div className="dictionary-tabs">
            <button
              className={`dictionary-tab ${dictionaryTab === 'canonical-terms' ? 'active' : ''}`}
              onClick={() => setDictionaryTab('canonical-terms')}
            >
              Canonical Terms
            </button>
            <button
              className={`dictionary-tab ${dictionaryTab === 'dictionary-entries' ? 'active' : ''}`}
              onClick={() => setDictionaryTab('dictionary-entries')}
            >
              Dictionary Entries
            </button>
          </div>

          {dictionaryTab === 'canonical-terms' && (
            <div className="dictionary-section">
              <div className="dictionary-section-header">
                <h2>Canonical Terms</h2>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleAddCanonicalTerm}
                >
                  + Add Canonical Term
                </button>
              </div>
              <CanonicalTermTable
                key={dictionaryRefreshKey}
                onEdit={handleEditCanonicalTerm}
                graphId={graphId}
              />
            </div>
          )}

          {dictionaryTab === 'dictionary-entries' && (
            <div className="dictionary-section">
              <div className="dictionary-section-header">
                <h2>Dictionary Entries</h2>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleAddDictionaryEntry}
                >
                  + Add Dictionary Entry
                </button>
              </div>
              <DictionaryEntryTable
                key={dictionaryRefreshKey}
                onEdit={handleEditDictionaryEntry}
                graphId={graphId}
              />
            </div>
          )}
        </div>
      ) : (
        /* Synapses and Nodes Section with Tabs */
        <div className="synapses-section">
          {/* Tabs */}
          <div className="graph-entities-tabs">
            <button
              type="button"
              className={`graph-entities-tab ${activeTab === 'synapses' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('synapses')}
            >
              Connections
            </button>
            <button
              type="button"
              className={`graph-entities-tab ${activeTab === 'nodes' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('nodes')}
            >
              Nodes
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'synapses' && (
            <SynapsesTable 
              ref={synapsesTableRef} 
              graphId={graphId}
              disableDrilldowns={subItem === 'expand-graph'}
            />
          )}
          {activeTab === 'nodes' && (
            <NodesTable 
              graphId={graphId}
              disableDrilldowns={subItem === 'expand-graph'}
            />
          )}
        </div>
      )}

      {/* Dictionary Modals */}
      {showCanonicalTermModal && (
        <CanonicalTermModal
          show={showCanonicalTermModal}
          onClose={() => {
            setShowCanonicalTermModal(false);
            setSelectedCanonicalTerm(null);
          }}
          onSave={handleCanonicalTermSaved}
          term={selectedCanonicalTerm}
          graphId={graphId}
        />
      )}

      {showDictionaryEntryModal && (
        <DictionaryEntryModal
          show={showDictionaryEntryModal}
          onClose={() => {
            setShowDictionaryEntryModal(false);
            setSelectedDictionaryEntry(null);
          }}
          onSave={handleDictionaryEntrySaved}
          entry={selectedDictionaryEntry}
          graphId={graphId}
        />
      )}

      {/* Graph Prompts Modal */}
      {showGraphPromptsModal && (
        <GraphPromptsModal
          isOpen={showGraphPromptsModal}
          onClose={() => {
            setShowGraphPromptsModal(false);
            // Navigate back to build page when closing
            navigate(`/user-home/graphs/build/${graphId}`);
          }}
          graphId={graphId}
          documents={[]}
          onPromptsUpdated={() => {}}
        />
      )}
    </div>
  );
}

export default BuildGraphPlaceholderPage;

